import { ChevronDownIcon } from '@components/Icons'
import cn from '@integrations/classnames'
import type { ComponentProps, ReactNode } from '@integrations/react'
import type { ISelectInputProps } from 'mmfintech-portal-commons'
import { Input as PortalInput } from 'mmfintech-portal-commons'

import styles from './Input.module.scss'

interface DropdownChevronProps {
  className?: string
  expanded?: boolean
  tone?: 'default' | 'contrast'
}

export function DropdownChevron({ className, expanded = false, tone = 'default' }: DropdownChevronProps) {
  return (
    <span
      className={cn(styles['dropdown-chevron'], 'dropdown-chevron', className, {
        [styles['expanded']]: expanded,
        expanded,
        [styles['dropdown-chevron--contrast']]: tone === 'contrast',
        'dropdown-chevron--contrast': tone === 'contrast'
      })}
      aria-hidden='true'
    >
      <ChevronDownIcon color='currentColor' />
    </span>
  )
}

interface DropdownOptionRowProps {
  title: ReactNode
  description?: ReactNode // smaller, muted second line
  icon?: ReactNode
  reserveIcon?: boolean // keep the icon column when there is no icon, so rows line up
}

/** A select option with an optional icon and description line; pass it as an option's `label`. */
export function DropdownOptionRow({ title, description, icon, reserveIcon = false }: DropdownOptionRowProps) {
  return (
    <span className={cn(styles['dropdown-option-row'], 'dropdown-option-row')}>
      {(icon || reserveIcon) && (
        <span className={cn(styles['dropdown-option-icon'], 'dropdown-option-icon')}>{icon}</span>
      )}
      <span className={cn(styles['dropdown-option-text'], 'dropdown-option-text')}>
        <span>{title}</span>
        {description && (
          <span className={cn(styles['dropdown-option-description'], 'dropdown-option-description')}>
            {description}
          </span>
        )}
      </span>
    </span>
  )
}

// react-select prevents the default of its own mouse presses, and an open date picker ignores such presses as outside
// clicks, so its calendar stayed open behind the menu. Opening a menu tells it a press happened elsewhere.
// The event goes to body (outside the React root), so no React handler sees it.
function notifyOutsidePress() {
  document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }))
}

type InputProps = ComponentProps<typeof PortalInput> & {
  dropdownPortal?: boolean
  dropdownVariant?: 'default' | 'filter' | 'compact'
  inputVariant?: 'default' | 'search'
  minMenuHeight?: number
  maxMenuHeight?: number
}

function Input({ inputVariant = 'default', ...props }: InputProps) {
  if (props.type !== 'select' && props.type !== 'custom-select') {
    const { parentClassName, ...inputProps } = props
    const searchFocusProps =
      inputVariant === 'search'
        ? { onFocus: 'onFocus' in inputProps && inputProps.onFocus ? inputProps.onFocus : () => undefined }
        : {}
    const nativeDateProps =
      props.type === 'date'
        ? {
            inputProps: {
              ...('inputProps' in props ? props.inputProps : {}),
              'data-test': props['data-test'],
              'aria-invalid': props['aria-invalid'],
              onKeyDown: 'onKeyDown' in props ? props.onKeyDown : undefined
            }
          }
        : {}
    const fieldProps = {
      ...inputProps,
      ...searchFocusProps,
      ...nativeDateProps,
      parentClassName: cn(
        inputVariant === 'search' && styles['input-wrapper--search'],
        inputVariant === 'search' && 'input-wrapper--search',
        parentClassName
      )
    } as ComponentProps<typeof PortalInput>
    return <PortalInput {...fieldProps} />
  }
  const {
    className,
    dropdownPortal = false,
    dropdownVariant = 'default',
    parentClassName,
    minMenuHeight,
    maxMenuHeight,
    presentation,
    ...selectProps
  } = props as ISelectInputProps &
    Pick<InputProps, 'dropdownPortal' | 'dropdownVariant' | 'minMenuHeight' | 'maxMenuHeight'>
  return (
    <PortalInput
      {...selectProps}
      {...(dropdownPortal
        ? {
            menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
            menuPosition: 'fixed' as const
          }
        : {})}
      presentation={{
        pointerCursor: true,
        minMenuHeight,
        maxMenuHeight,
        containerAttributes: { 'data-test': props['data-test'] },
        renderDropdownIndicator: (expanded) => <DropdownChevron expanded={expanded} />,
        ...(dropdownPortal && {
          portalZIndex: 10001,
          getMenuAnchor: (control) =>
            control.closest<HTMLDivElement>('.filter-pill') ??
            control.closest<HTMLDivElement>('.input-container') ??
            control
        }),
        ...presentation,
        onMenuOpen: () => {
          notifyOutsidePress()
          presentation?.onMenuOpen?.()
        }
      }}
      className={cn(
        styles['dropdown-input'],
        'dropdown-input',
        styles[`dropdown-input--${dropdownVariant}`],
        `dropdown-input--${dropdownVariant}`,
        {
          [styles['dropdown-input--multi']]: selectProps.isMultiSelect,
          'dropdown-input--multi': selectProps.isMultiSelect
        },
        className
      )}
      parentClassName={cn(
        styles['dropdown-input-wrapper'],
        'dropdown-input-wrapper',
        styles[`dropdown-input-wrapper--${dropdownVariant}`],
        `dropdown-input-wrapper--${dropdownVariant}`,
        parentClassName
      )}
    />
  )
}

export default Input
